import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Funnel, ArrowsClockwise, ChartBar, Warning } from "@phosphor-icons/react";

// Conversion funnel widget for /admin — reads counts from Plausible via the
// backend proxy so the API key stays server-side.
// Steps mirror the trackEvent() calls scattered through the frontend.
const RANGES = [
  { value: "7d",   label: "7 giorni" },
  { value: "30d",  label: "30 giorni" },
  { value: "12mo", label: "12 mesi" },
  { value: "all",  label: "Da sempre" },
];

export default function PlausibleFunnelWidget({ token }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(
        `${base}/api/admin/plausible-funnel?date_range=${range}`,
        { headers: { "X-Admin-Token": token } },
      );
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore nel caricamento del funnel.");
    } finally {
      setLoading(false);
    }
  }, [range, token]);

  useEffect(() => { load(); }, [load]);

  const maxVisitors = data?.steps?.reduce(
    (m, s) => Math.max(m, s.visitors), 0,
  ) || 0;

  return (
    <div
      data-testid="admin-plausible-funnel"
      className="rounded-2xl border border-border bg-surface-card p-6 space-y-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-terracotta/10 flex items-center justify-center">
            <Funnel size={20} weight="duotone" className="text-terracotta" />
          </div>
          <div>
            <h2 className="font-display font-black text-xl text-ink tracking-tight">
              Conversion funnel
            </h2>
            <p className="text-xs text-ink-soft font-sans mt-0.5">
              Dati da Plausible &middot; sito <span className="font-mono">{data?.site_id || "—"}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="funnel-range-select"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="rounded-full border border-border bg-surface px-3 py-1.5 text-sm font-sans font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-terracotta/40"
          >
            {RANGES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <button
            data-testid="funnel-reload"
            onClick={load}
            disabled={loading}
            aria-label="Ricarica"
            className="h-8 w-8 rounded-full border border-border bg-surface text-ink-soft hover:text-ink hover:bg-surface-alt transition-colors flex items-center justify-center disabled:opacity-50"
          >
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="funnel-error" className="text-sm text-terracotta flex items-center gap-1.5 font-sans">
          <Warning size={14} weight="bold" /> {error}
        </div>
      )}

      {data?.error && (
        <div className="rounded-xl bg-terracotta/5 border border-terracotta/30 p-3 text-xs text-ink font-sans">
          Plausible ha risposto con un errore: <span className="font-mono">{data.error}</span>
        </div>
      )}

      {data && !data.configured && (
        <div className="rounded-xl bg-sand/30 border border-sand/50 p-3 text-xs text-ink font-sans">
          ⚠️ Plausible non configurato. Manca <span className="font-mono">PLAUSIBLE_API_KEY</span> o <span className="font-mono">PLAUSIBLE_SITE_ID</span> nel backend.
        </div>
      )}

      {data?.configured && maxVisitors === 0 && !loading && (
        <div className="rounded-xl bg-sage/10 border border-sage/30 p-3 text-xs text-ink font-sans">
          Nessun evento registrato in questo periodo. Gli eventi vengono raccolti solo dal dominio <span className="font-mono">exchangeplan.app</span>.
        </div>
      )}

      <div className="space-y-3">
        {data?.steps?.map((s, idx) => {
          const widthPct = maxVisitors ? (s.visitors / maxVisitors) * 100 : 0;
          const dropLabel = idx === 0 || s.conversion_from_prev == null
            ? null
            : `${s.conversion_from_prev}% dallo step precedente`;
          return (
            <div
              key={s.name}
              data-testid={`funnel-step-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
              className="space-y-1.5"
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-terracotta/10 text-terracotta font-display font-black text-xs flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <span className="font-sans font-semibold text-ink truncate">{s.label}</span>
                </div>
                <div className="flex items-baseline gap-2 shrink-0">
                  <span className="font-display font-black text-2xl text-ink" data-testid={`funnel-visitors-${idx}`}>
                    {s.visitors}
                  </span>
                  <span className="text-[11px] uppercase tracking-widest font-sans font-bold text-ink-soft">
                    visitatori
                  </span>
                </div>
              </div>
              <div className="h-2 bg-surface-alt rounded-full overflow-hidden">
                <div
                  className="h-full bg-terracotta transition-[width] duration-500 ease-out"
                  style={{ width: `${widthPct}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-[11px] font-sans text-ink-soft">
                <span>{s.events} eventi totali</span>
                {dropLabel && <span className="italic">{dropLabel}</span>}
                {idx === 0 && s.visitors > 0 && (
                  <span className="italic">100% (start)</span>
                )}
                {idx > 0 && s.conversion_from_start != null && (
                  <span className="font-mono">{s.conversion_from_start}% del totale</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {data && (
        <div className="pt-2 border-t border-border/60 text-[11px] text-ink-soft font-sans flex items-center gap-1.5">
          <ChartBar size={12} weight="bold" />
          Periodo: {RANGES.find(r => r.value === data.date_range)?.label || data.date_range}
        </div>
      )}
    </div>
  );
}
